<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'

	import HasPermission from '@/components/HasPermission.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import StyledButton from '@/components/StyledButton.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useRefChangeTracker from '@/composables/useRefChangeTracker'

	import PasskeysManager from '@/components/PasskeysManager.vue'
	import EditUserPassword from '@/modules/users/components/EditUserPassword.vue'
	import useUser from '@/modules/users/composables/useUser'
	import type { User } from '@/modules/users/interfaces/user'
	import { computed } from 'vue'
	import { useToast } from 'vue-toastification'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()
	const queryClient = useQueryClient()

	const { user: authUser } = useAuthUser()
	const isOwnProfile = computed(() => authUser.value?.id === props.user.id)

	const {
		value: username,
		hasChanged,
		resetInitialValue,
	} = useRefChangeTracker(props.user.username ?? '')
	const { updateUser, updateUserLoading } = useUser({ immediate: false })
	const toast = useToast()

	const handleSaveUsername = async () => {
		updateUser(
			{ id: props.user.id, username: username.value },
			{
				onError: (error: any) => {
					toast.error(
						error?.response?.data?.message ||
							'Error al actualizar el usuario. Intente nuevamente.'
					)
				},
			}
		)
		resetInitialValue()

		await queryClient.invalidateQueries(['user', props.user.id])
		await queryClient.refetchQueries(['user', props.user.id])
	}
</script>

<template>
	<RoundedBox class="bg-white">
		<form
			@submit.prevent="handleSaveUsername"
			class="grid gap-[1.563rem]"
		>
			<h2 class="text-xl font-medium">Ajustes generales</h2>

			<div class="grid gap-5 pb-2">
				<!-- <StyledInput
					v-model="username"
					label="Nombre de usuario"
					placeholder="ej. usuario123"
				/> -->

				<HasPermission
					name-permission="Update users companies"
					:entity-type="'App\\Models\\Company'"
				>
					<StyledButton
						:disabled="!hasChanged"
						:loading="updateUserLoading"
						:label="hasChanged ? 'Guardar' : 'Sin cambios'"
						class="bg-[#253C70] text-white ml-auto"
						type="submit"
					/>
				</HasPermission>
			</div>
		</form>

		<EditUserPassword
			:user-id="user.id"
			:user-email="user.email"
			:name-permission-update="'update-users'"
			:entity-type="'App\\Models\\Company'"
		/>

		<div v-if="isOwnProfile" class="border-t border-gray-100 pt-5 mt-2">
			<PasskeysManager />
		</div>

		<!-- <EditUserEmails
      :user-id="user.id"
      :main-email="user.email"
      :emails="user.emails || []"
    /> -->
	</RoundedBox>
</template>
